// Component to render User Growth Chart
import {
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Bar,
  ComposedChart,
} from "recharts";
const UserGrowthChart = ({ data, interval }) => {
  // Function to format User Growth data for chart
  const formatUserGrowthDataForChart = (data, intervalLabel) => {
    if (!data || !Array.isArray(data) || data.length === 0) {
      return [];
    }

    const firstItem = data[0];

    // Check if it's year data (4 elements: year, new_users, previous_users, growth_rate)
    if (
      firstItem.length === 4 &&
      typeof firstItem[0] === "number" &&
      firstItem[0] > 1000
    ) {
      return data
        .map((item) => ({
          name: item[0].toString(),
          newUsers: item[1],
          previousUsers: item[2],
          growthRate: item[3],
          label: `${item[0]}`,
        }))
        .reverse(); // Reverse to show chronological order
    }

    // Check if it's month data (6 elements: year, month_num, month_name, new_users, previous_users, growth_rate)
    if (firstItem.length === 6) {
      return data
        .map((item) => ({
          name: `${item[2].trim()} ${item[0]}`,
          newUsers: item[3],
          previousUsers: item[4],
          growthRate: item[5],
          label: `${item[2].trim()} ${item[0]}`,
        }))
        .reverse();
    }

    // Check if it's date/time data (4 elements: timestamp, new_users, previous_users, growth_rate)
    if (firstItem.length === 4 && typeof firstItem[0] === "string") {
      return data
        .map((item) => {
          const dateStr = item[0];
          let displayName;

          if (dateStr.includes(" ")) {
            const date = new Date(dateStr);
            if (intervalLabel.includes("Minute")) {
              displayName = date.toLocaleTimeString("en-US", {
                hour: "2-digit",
                minute: "2-digit",
              });
            } else if (intervalLabel.includes("Hour")) {
              displayName = date.toLocaleTimeString("en-US", {
                hour: "2-digit",
                hour12: true,
              });
            } else {
              displayName = date.toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
              });
            }
          } else if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
            const date = new Date(dateStr);
            displayName = date.toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
            });
          } else {
            displayName = dateStr;
          }

          return {
            name: displayName,
            newUsers: item[1],
            previousUsers: item[2],
            growthRate: item[3],
            label: displayName,
          };
        })
        .reverse();
    }

    return [];
  };

  // Custom Tooltip for User Growth Chart
  const UserGrowthTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div
          style={{
            backgroundColor: "white",
            border: "1px solid #e2e8f0",
            borderRadius: "8px",
            padding: "12px",
            fontSize: "12px",
          }}
        >
          <p
            style={{ margin: "0 0 8px 0", fontWeight: "600", color: "#2d3748" }}
          >
            {label}
          </p>
          <p style={{ margin: "4px 0", color: "#667eea" }}>
            <strong>New Users:</strong> {data.newUsers?.toLocaleString() || 0}
          </p>
          {data.previousUsers !== null && data.previousUsers !== undefined && (
            <p style={{ margin: "4px 0", color: "#718096" }}>
              <strong>Previous Period:</strong>{" "}
              {data.previousUsers.toLocaleString()}
            </p>
          )}
          {data.growthRate !== null && data.growthRate !== undefined && (
            <p
              style={{
                margin: "4px 0",
                color: data.growthRate >= 0 ? "#38a169" : "#e53e3e",
                fontWeight: "600",
              }}
            >
              <strong>Growth Rate:</strong> {data.growthRate.toFixed(2)}%
            </p>
          )}
        </div>
      );
    }
    return null;
  };

  const chartData = formatUserGrowthDataForChart(data, interval);

  if (!chartData || chartData.length === 0) {
    return (
      <div className="chart-placeholder">
        📊 No data available
        <small>Try selecting a different date range</small>
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={250}>
      <ComposedChart
        data={chartData}
        margin={{ top: 5, right: 20, left: 0, bottom: 5 }}
      >
        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
        <XAxis
          dataKey="name"
          stroke="#718096"
          style={{ fontSize: "12px" }}
          angle={chartData.length > 10 ? -45 : 0}
          textAnchor={chartData.length > 10 ? "end" : "middle"}
          height={chartData.length > 10 ? 60 : 30}
        />
        <YAxis
          yAxisId="left"
          stroke="#718096"
          style={{ fontSize: "12px" }}
          allowDecimals={false}
          label={{
            value: "Users",
            angle: -90,
            position: "insideLeft",
            style: { fontSize: "11px" },
          }}
        />
        <YAxis
          yAxisId="right"
          orientation="right"
          stroke="#48bb78"
          style={{ fontSize: "12px" }}
          label={{
            value: "Growth %",
            angle: 90,
            position: "insideRight",
            style: { fontSize: "11px" },
          }}
        />
        <Tooltip content={<UserGrowthTooltip />} />
        <Legend wrapperStyle={{ fontSize: "12px" }} />
        <Bar
          yAxisId="left"
          dataKey="newUsers"
          fill="#667eea"
          name="New Users"
          radius={[8, 8, 0, 0]}
        />
        <Line
          yAxisId="right"
          type="monotone"
          dataKey="growthRate"
          stroke="#48bb78"
          strokeWidth={2}
          dot={{ fill: "#48bb78", r: 4 }}
          activeDot={{ r: 6 }}
          name="Growth Rate %"
        />
      </ComposedChart>
    </ResponsiveContainer>
  );
};

export default UserGrowthChart;
